// src/contexts/DataContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { fetchCompetitions, fetchSubmissions } from '@/services/apiService';
import { useAuth } from './AuthContext';

const DataContext = createContext(null);

// 🔸 Provider wraps your entire app and shares competition-related data globally
export const DataProvider = ({ children }) => {
  const [competitions, setCompetitions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth(); // Access logged-in user from AuthContext

  // 🔹 Fetches competition list from backend
  const fetchCompetitionsData = async () => {
    setLoading(true);
    try {
      const data = await fetchCompetitions();
      setCompetitions(data);
    } catch (error) {
      console.error('Error fetching competitions:', error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Fetches all submissions (used elsewhere, not triggered here)
  const fetchSubmissionsData = async (id) => {
    try {
      const data = await fetchSubmissions(id);
      setSubmissions(data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  // ✅ Only fetch competitions if:
  //    A) The user is logged in
  //    B) The access token has been saved
  useEffect(() => {
    const token = sessionStorage.getItem('accessToken');
    if (user && token && competitions.length === 0) {
      fetchCompetitionsData();
    }
  }, [user]); // 🔁 Only runs when the user value changes (e.g. on login)

  return (
    <DataContext.Provider value={{
      competitions,
      setCompetitions,     // 🔄 Allows updates from pages like CompetitionsList
      submissions,
      loading,
      user
    }}>
      {children}
    </DataContext.Provider>
  );
};

// 🔹 useData() gives easy access to this shared context anywhere in the app
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};